// src/components/instructor/OrgClientsWorkspace.js
//
// Pass 50A (items 14–17) — the organisation Clients tab renders ONLY the client workflow
// (NOT the giant legacy OrganizationDashboardPage). Clients are derived from canonical bookings
// (+ cases); Retention/Follow-up lives inside Clients. The full retention workspace remains at
// /retention and is deep-linked from here.

import React, { useMemo, useState } from 'react';
import { deriveClients, activeSeasonClients } from '../../utils/clientsWorkspace';
import { scopeToSeason } from '../../utils/seasonScope';
import { isClientBooking, isLiveBooking } from '../../utils/scheduleModel';
import { CURRENT_SEASON_ID } from '../../utils/instructorCategory';
import { opsDateKey } from '../../utils/opsTime';

const V = { t1: '#1A1917', t2: '#6B6860', t3: '#9E9B95', border: '#E2E0DB', s2: '#F2F1EE', green: '#1D9E75', amber: '#C97D10' };
const F = "'DM Sans',sans-serif";

const fmt = (ms) => (ms ? opsDateKey(new Date(ms)) : '—');

export default function OrgClientsWorkspace({ bookings = [], members = [], cases = [] }) {
  const [view, setView] = useState('all'); // 'all' | 'active' | 'upcoming'
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const clients = useMemo(() => deriveClients(bookings, members, cases), [bookings, members, cases]);
  // Pass 58G1 — canonical, trustworthy filters only. "Active this season" is an ACTIVITY property of the
  // ONE canonical client index (activeSeasonClients — bookingIds ∩ live current-season client bookings),
  // NOT a second identity derivation (which could drop a case-linked client). Same helper as the Overview,
  // so both show identical counts. "Upcoming" = a real future booking (nextBookingAt).
  const seasonLiveClient = useMemo(
    () => scopeToSeason(bookings, CURRENT_SEASON_ID).filter(isLiveBooking).filter(isClientBooking),
    [bookings],
  );
  const upcoming = useMemo(() => clients.filter((c) => c.nextBookingAt), [clients]);
  const active = useMemo(() => activeSeasonClients(clients, seasonLiveClient), [clients, seasonLiveClient]);
  const base = view === 'active' ? active : view === 'upcoming' ? upcoming : clients;
  const rows = base.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q);
  });

  const tab = (k, l, n) => (
    <button key={k} data-testid={`clients-view-${k}`} onClick={() => setView(k)}
      style={{ fontFamily: F, fontSize: 12.5, fontWeight: 700, padding: '6px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', background: view === k ? '#fff' : 'transparent', color: view === k ? V.t1 : V.t3 }}>
      {l}{n != null ? ` (${n})` : ''}
    </button>
  );
  const cell = { padding: '8px 10px', borderBottom: `1px solid ${V.border}`, fontSize: 12.5, textAlign: 'left' };

  if (selected) {
    const client = clients.find((c) => c.clientKey === selected);
    // §K resolve detail from the canonical client index (linked booking ids), NOT a second
    // email/name identity rule in the component (which could mix namesakes).
    const ids = new Set(client?.bookingIds || []);
    const clientBookings = bookings.filter((b) => ids.has(b.id));
    // Pass 79 §25/§48 — extract the EXPLICIT commercial clientId ONLY when the canonical key is `client:<clientId>`.
    // A user/email/phone/name-only key is NOT a commercial clientId, so no CRM link is offered (never fabricate one).
    const explicitClientId = String(client?.clientKey || '').startsWith('client:') ? String(client.clientKey).slice('client:'.length) : '';
    const orgId = clientBookings[0]?.organizationId || bookings[0]?.organizationId || '';
    return (
      <div style={{ fontFamily: F, color: V.t1 }} data-testid="client-detail">
        <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: V.t2, cursor: 'pointer', fontSize: 13, marginBottom: 10 }}>‹ All clients</button>
        <h2 style={{ margin: '0 0 4px', fontSize: 20 }}>{client?.name || 'Client'}</h2>
        {explicitClientId && orgId ? (
          <a data-testid="open-client-crm" href={`/org/${orgId}/clients/${explicitClientId}`} style={{ display: 'inline-block', marginBottom: 10, fontSize: 12.5, fontWeight: 700, color: V.green, textDecoration: 'none' }}>
            Open full CRM →
          </a>
        ) : null}
        <div style={{ fontSize: 13, color: V.t2, marginBottom: 14 }}>{client?.email || '—'}{client?.phone ? ` · ${client.phone}` : ''} · {client?.totalSessions || 0} sessions · {client?.cases || 0} case(s)</div>
        <div style={{ fontSize: 11, fontWeight: 700, color: V.t3, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Booking / session history</div>
        <div className="m-innerscroll"><table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
          <thead><tr style={{ color: V.t3, fontSize: 11, textTransform: 'uppercase' }}><th style={cell}>Date</th><th style={cell}>Service</th><th style={cell}>Instructor</th><th style={cell}>Payment</th></tr></thead>
          <tbody>
            {clientBookings.sort((a, b) => String(b.serviceDate || '').localeCompare(String(a.serviceDate || ''))).map((b) => (
              <tr key={b.id}><td style={cell}>{b.serviceDate || fmt(b.startAt?.seconds ? b.startAt.seconds * 1000 : Date.parse(b.startAt))}</td><td style={cell}>{b.serviceType || b.serviceNameSnapshot || 'Lesson'}</td><td style={cell}>{b.instructorNameSnapshot || b.instructorId || 'Unassigned'}</td><td style={cell}>{b.paymentState || '—'}</td></tr>
            ))}
            {!clientBookings.length && <tr><td style={cell} colSpan={4}>No bookings yet.</td></tr>}
          </tbody>
        </table></div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: F, color: V.t1 }} data-testid="org-clients-workspace">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Clients</h2>
        <div style={{ display: 'flex', gap: 2, background: V.s2, border: `1px solid ${V.border}`, borderRadius: 8, padding: 2 }}>
          {tab('all', 'All clients', clients.length)}
          {tab('active', 'Active 2026/27', active.length)}
          {tab('upcoming', 'Upcoming', upcoming.length)}
        </div>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name / email" style={{ padding: '6px 10px', borderRadius: 8, border: `1px solid ${V.border}`, fontSize: 13 }} />
      </div>

      <div className="m-innerscroll"><table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
        <thead>
          <tr style={{ color: V.t3, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            <th style={cell}>Client</th><th style={cell}>Contact</th><th style={cell}>Last booking</th><th style={cell}>Next booking</th><th style={cell}>Sessions</th><th style={cell}>Recent instructor</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.clientKey} data-testid="client-row" onClick={() => setSelected(c.clientKey)} style={{ cursor: 'pointer' }}>
              <td style={{ ...cell, fontWeight: 600 }}>{c.name || '—'}{String(c.clientKey || '').startsWith('client:') ? <span title="Stable client identity (linked across cases and seasons)" style={{ color: V.green, marginLeft: 5, fontSize: 10 }}>●</span> : null}</td>
              <td style={cell}>{c.email || c.phone || '—'}</td>
              <td style={cell}>{fmt(c.lastBookingAt)}</td>
              <td style={cell}>{c.nextBookingAt ? fmt(c.nextBookingAt) : <span style={{ color: V.amber }}>none</span>}</td>
              <td style={cell}>{c.totalSessions}</td>
              <td style={cell}>{c.nextInstructorName || c.lastInstructorName || '—'}{c.lowConfidence ? <span title="Low-confidence name-only match" style={{ color: V.amber, marginLeft: 4 }}>~</span> : null}</td>
            </tr>
          ))}
          {!rows.length && <tr><td style={cell} colSpan={6}>{view === 'followup' ? 'No clients need follow-up.' : 'No clients yet.'}</td></tr>}
        </tbody>
      </table></div>
    </div>
  );
}
